import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import NavBar from "../components/NavBar";
import ReferralsTable from "../components/ReferralsTable";
import CreateReferralModal from "../components/CreateReferralModal";
import ReferralDetailsModal from "../components/ReferralDetailsModal";
import {
  ChevronLeftIcon,
  PlusIcon,
  SendIcon,
} from "../components/icons";
import { Skeleton, TableRowSkeleton } from "../components/Skeleton";

// Per-client matches detail page. Reached from the Matches Tracking
// index by clicking a client card. Shows:
//   - a sticky header with the client identity
//   - the full referral table for just this client
//   - "Create Referral" pre-scoped to this client
//
// We fetch all of this client's referrals up-front (the per-client
// dataset is small — a single client usually has 1-50 matches) and
// skip server pagination here. If that ever becomes an issue we can
// add it back without changing the URL contract.
const ClientReferrals = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { setGlobalLoadingMessage } = useLoading();

  const [client, setClient] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Pull a big page — per-client referral counts are bounded; this
      // is one round-trip instead of N paginated ones. `limit=1000`
      // is well above any realistic per-client total.
      const params = new URLSearchParams({
        clientId,
        limit: "1000",
        page: "1",
      });
      const [refResp, clientResp] = await Promise.all([
        apiFetch(`/referrals/bureau?${params}`),
        apiFetch(`/clients/${clientId}`).catch(() => null),
      ]);
      setReferrals(refResp.data || []);
      // /clients/:id returns the unified PublicUser shape with the
      // `user` alias. Fall back to the populated client from the first
      // referral if the dedicated fetch isn't wired or fails.
      const fromClient = clientResp?.data || null;
      const fromReferral = refResp.data?.[0]?.client || null;
      setClient(fromClient || fromReferral);
    } catch (err) {
      console.error("Failed to load client matches:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (referralData) => {
    try {
      setGlobalLoadingMessage("Creating match...");
      await apiFetch("/referrals/bureau", {
        method: "POST",
        body: JSON.stringify(referralData),
      });
      setIsCreateOpen(false);
      fetchData();
    } catch (err) {
      console.error("Failed to create referral:", err);
      throw err;
    } finally {
      setGlobalLoadingMessage("");
    }
  };

  const handleDelete = async (referralId) => {
    if (!window.confirm("Delete this match?")) return;
    try {
      setGlobalLoadingMessage("Deleting match...");
      await apiFetch(`/referrals/bureau/${referralId}`, { method: "DELETE" });
      fetchData();
    } catch (err) {
      console.error("Failed to delete:", err);
      alert("Failed to delete: " + err.message);
    } finally {
      setGlobalLoadingMessage("");
    }
  };

  if (loading && referrals.length === 0 && !client) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Client header placeholder — mimics the real layout (avatar
              square, name line, sub-info, total) so the page doesn't
              jump when the actual data lands. */}
          <Skeleton className="h-4 w-40 mb-4" />
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 mb-6 flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-5 w-1/3 mb-2" />
              <Skeleton className="h-3 w-1/2 mb-1" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          {/* Table placeholder — same column count as the real table
              when both the client column is hidden (we know that here)
              and the action column is hidden during initial load. */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
            <table className="w-full">
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} columns={3} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // The client object can arrive in two shapes depending on the source
  // (dedicated /clients fetch vs. populated ref on a referral). The
  // unified shape exposes the same fields at the top level OR under a
  // legacy `user` alias — coalesce so the header renders either way.
  const clientName =
    client?.fullName || client?.user?.fullName || "(Unnamed client)";
  const clientRef =
    client?.referenceId || client?.user?.referenceId || "—";
  const clientPhone =
    client?.contactNumber || client?.user?.contactNumber || "";
  const clientCity = client?.city || client?.user?.city || "";

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate("/bureau/referrals")}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-4"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Matches Tracking
        </button>

        {/* Client header */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <SendIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-[var(--color-text-strong)] truncate">
                {clientName}
              </h1>
              <p className="text-xs text-[var(--color-text-muted)] truncate">
                {clientRef}
                {clientCity ? ` • ${clientCity}` : ""}
                {clientPhone ? ` • ${clientPhone}` : ""}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                {referrals.length} match{referrals.length === 1 ? "" : "es"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors text-sm font-semibold"
          >
            <PlusIcon className="w-4 h-4" />
            Create Match
          </button>
        </div>

        {error && <div className="text-red-500 text-center py-4">{error}</div>}

        <ReferralsTable
          referrals={referrals}
          onRowClick={(r) => setSelectedReferral(r)}
          onDelete={handleDelete}
          showClient={false}
          emptyMessage="No matches for this client yet."
        />
      </div>

      <CreateReferralModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreateReferral={handleCreate}
        preselectedClient={client}
      />

      <ReferralDetailsModal
        isOpen={Boolean(selectedReferral)}
        referral={selectedReferral}
        onClose={() => setSelectedReferral(null)}
        onUpdate={fetchData}
      />
    </div>
  );
};

export default ClientReferrals;
